import axios from "axios";
import { USER_API } from "config";
import { authClient } from "lib/auth-client";
import { getBearerSessionToken, setBearerSessionToken } from "lib/better-auth-session";
import {
  assertAuthSessionUnchanged,
  captureAuthSession,
  type AuthSessionOwner,
} from "lib/auth-session-ownership";
import type { UserInfoDataType } from "store/types/auth";
import type { ProfileType } from "store/types/profile";
import {
  assertAuthAttemptCurrent,
  authAttemptFetchOptions,
  beginAuthAttempt,
  getAuthAttemptForResult,
  tagAuthAttemptResult,
  type AuthAttempt,
} from "lib/auth-attempt";

type MeResponse = {
  user?: ProfileType;
} & ProfileType;

const loginOwners = new WeakMap<object, AuthSessionOwner>();

export function tagBetterAuthLoginResult<T extends object>(result: T, owner: AuthSessionOwner): T {
  loginOwners.set(result, owner);
  return result;
}

/** Keep ownership across a caller's final await before applying login data. */
export function assertBetterAuthLoginCurrent(userInfo: object): void {
  const attempt = getAuthAttemptForResult(userInfo);
  if (attempt) {
    assertAuthAttemptCurrent(attempt);
  }
  const owner = loginOwners.get(userInfo);
  // A wrapper's newer tag cannot erase the owner of an already-built result.
  if (owner && !attempt?.applied) assertAuthSessionUnchanged(owner);
}

function assertLoginOwnerCurrent(owner: AuthSessionOwner, attempt?: AuthAttempt): void {
  assertAuthSessionUnchanged(owner);
  if (attempt) assertAuthAttemptCurrent(attempt);
}

function extractSessionToken(
  sessionData:
    | {
        session?: { token?: string | null } | null;
      }
    | null
    | undefined
): string | null {
  const token = sessionData?.session?.token;
  return typeof token === "string" && token.length > 0 ? token : null;
}

export async function resolveBetterAuthSessionToken(
  owner = captureAuthSession(),
  attempt?: AuthAttempt
): Promise<string | null> {
  assertLoginOwnerCurrent(owner, attempt);
  const cached = getBearerSessionToken();
  if (cached) {
    return cached;
  }

  const sessionResult = await authClient.getSession(
    attempt ? { fetchOptions: authAttemptFetchOptions(attempt) } : undefined
  );
  assertLoginOwnerCurrent(owner, attempt);
  const token = extractSessionToken(sessionResult.data);
  if (token) {
    setBearerSessionToken(token);
    return token;
  }

  return null;
}

export async function buildUserInfoFromBetterAuthSession(
  owner = captureAuthSession(),
  attempt: AuthAttempt = beginAuthAttempt()
): Promise<UserInfoDataType> {
  const token = await resolveBetterAuthSessionToken(owner, attempt);
  assertLoginOwnerCurrent(owner, attempt);
  if (!token) {
    throw new Error("No Better Auth session");
  }

  const response = await axios.get<{ data: MeResponse }>(USER_API.me, {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 30_000,
  });

  assertLoginOwnerCurrent(owner, attempt);
  const payload = response.data.data ?? (response.data as unknown as MeResponse);
  const user = payload.user ?? payload;

  const userInfo: UserInfoDataType = {
    status_code: 200,
    message: "OK",
    data: {
      access_token: token,
      refresh_token: token,
      user,
    },
  };
  tagBetterAuthLoginResult(userInfo, owner);
  return tagAuthAttemptResult(userInfo, attempt);
}
